import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import BaseModal from "../BaseModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function LoyaltyProgramHelpDialog({ isOpen, onClose }: Props) {
  return (
    <BaseModal
      open={isOpen}
      closeModal={onClose}
      allowOverflowY={true}
      title={
        <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Loyalty Program Guide
            </h1>
            <p className="mt-1 text-gray-300">
              Choose the right combination for your business
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-primary text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary"
          >
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      }
      parentDivStyles="inline-block transform overflow-hidden max-w-6xl rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="p-8">
        <div className="max-h-[70vh] overflow-y-auto">
          {/* Overview */}
          <div className="mb-8 border-b border-gray-200 pb-6">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              Quick Overview
            </h3>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-700">
                <strong className="text-gray-900">Program Type</strong> = The
                overall strategy of your loyalty program
                <br />
                <strong className="text-gray-900">Reward Type</strong> = What
                customers actually receive as rewards
              </p>
            </div>
          </div>

          {/* Recommended Combinations */}
          <div className="mb-8">
            <div className="mb-6 flex items-center gap-2">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Recommended Combinations
              </h3>
              <span className="text-sm text-gray-500">
                (Ready to use today)
              </span>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Points + Points */}
              <div className="relative rounded-lg border border-gray-200 p-6">
                <div className="absolute right-4 top-4">
                  <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
                    ⭐ MOST POPULAR
                  </span>
                </div>
                <h4 className="mb-2 font-semibold text-gray-900">
                  Points-Based + Points
                </h4>
                <p className="mb-3 text-sm text-gray-600">
                  <strong>Perfect for:</strong> Traditional "earn points, redeem
                  later" programs
                </p>
                <div className="space-y-2 text-sm text-gray-700">
                  <div>
                    <strong>How it works:</strong> Customer spends $100 → Earns
                    200 points
                  </div>
                  <div>
                    <strong>Customer redeems:</strong> Points for gift cards,
                    products, or services
                  </div>
                  <div>
                    <strong>Why it's great:</strong> Easy to understand
                  </div>
                </div>
              </div>

              {/* Discount + Percentage */}
              <div className="rounded-lg border border-gray-200 p-6">
                <h4 className="mb-2 font-semibold text-gray-900">
                  Discount-Based + Percentage
                </h4>
                <p className="mb-3 text-sm text-gray-600">
                  <strong>Perfect for:</strong> Service businesses wanting
                  repeat visits
                </p>
                <div className="space-y-2 text-sm text-gray-700">
                  <div>
                    <strong>How it works:</strong> Customer pays $500 → Earns
                    10% discount
                  </div>
                  <div>
                    <strong>Why it's great:</strong> Customers still need to pay
                    for future services but will be incentivized to do so
                  </div>
                </div>
              </div>

              {/* Discount + Fixed */}
              <div className="rounded-lg border border-gray-200 p-6">
                <h4 className="mb-2 font-semibold text-gray-900">
                  Discount-Based + Fixed Amount
                </h4>
                <p className="mb-3 text-sm text-gray-600">
                  <strong>Perfect for:</strong> Promoting specific spending
                  thresholds
                </p>
                <div className="space-y-2 text-sm text-gray-700">
                  <div>
                    <strong>How it works:</strong> Customer spends $150 → Earns
                    $25 off
                  </div>
                  <div>
                    <strong>Why it's great:</strong> Crystal clear value for
                    customers
                  </div>
                </div>
              </div>

              {/* Cashback + Commission */}
              <div className="rounded-lg border border-gray-200 p-6">
                <h4 className="mb-2 font-semibold text-gray-900">
                  Cashback + Commission
                </h4>
                <p className="mb-3 text-sm text-gray-600">
                  <strong>Perfect for:</strong> High-value services and "rebate"
                  programs
                </p>
                <div className="space-y-2 text-sm text-gray-700">
                  <div>
                    <strong>How it works:</strong> Customer pays $800 → Gets $40
                    back
                  </div>
                  <div>
                    <strong>Why it's great:</strong> Real money motivates
                    customers to act quickly
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Coming Soon */}
          <div className="mb-8">
            <div className="mb-6 flex items-center gap-2">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                Coming Soon
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-gray-200 p-4 opacity-75">
                <h4 className="mb-2 font-medium text-gray-900">
                  Referral Programs
                </h4>
                <p className="text-sm text-gray-600">
                  Customer refers friend → Both get rewards when friend pays
                  first invoice
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 opacity-75">
                <h4 className="mb-2 font-medium text-gray-900">
                  Free Service Rewards
                </h4>
                <p className="text-sm text-gray-600">
                  Earn free services after spending thresholds
                </p>
              </div>
            </div>
          </div>

          {/* Pro Tips */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Pro Tips for Success
            </h3>
            <div className="grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
              <div className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-gray-400"></div>
                <div>
                  <strong className="text-gray-900">Start simple:</strong>
                  <span className="text-gray-600">
                    {" "}
                    Points-based programs are easiest for customers
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-gray-400"></div>
                <div>
                  <strong className="text-gray-900">Set clear limits:</strong>
                  <span className="text-gray-600">
                    {" "}
                    Use reward limits to control costs
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-gray-400"></div>
                <div>
                  <strong className="text-gray-900">
                    Consider your model:
                  </strong>
                  <span className="text-gray-600">
                    {" "}
                    High-frequency services work great with discounts
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-gray-400"></div>
                <div>
                  <strong className="text-gray-900">Test first:</strong>
                  <span className="text-gray-600">
                    {" "}
                    Start with one program and expand
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3 md:col-span-2">
                <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-gray-400"></div>
                <div>
                  <strong className="text-gray-900">Make it visible:</strong>
                  <span className="text-gray-600">
                    {" "}
                    Include information about rewards in your marketing and on
                    your website
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
          <button
            type="button"
            className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            onClick={onClose}
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
